import posthog from 'posthog-js';
import { supabase } from './supabase';

export interface GeoInfo {
  ip: string;
  city?: string;
  region?: string;
  country?: string;
  isp?: string;
}

export interface LoginAuditRecord {
  id?: number;
  user_id?: string;
  email: string;
  user_name?: string;
  role?: string;
  ip_address: string;
  city?: string;
  region?: string;
  country?: string;
  isp?: string;
  user_agent?: string;
  device_type?: string;
  browser?: string;
  login_at?: string;
}

// 1. Initialize PostHog (if configured via environment variables)
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

let isPostHogInitialized = false;

export function initTracker() {
  if (typeof window === 'undefined') return;

  if (POSTHOG_KEY && !isPostHogInitialized) {
    try {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        autocapture: true,
        capture_pageview: true,
        session_recording: {
          maskAllInputs: false,
        },
      });
      isPostHogInitialized = true;
      console.log('✅ [Tracker] PostHog external analytics initialized successfully.');
    } catch (err) {
      console.warn('⚠️ [Tracker] Could not initialize PostHog:', err);
    }
  } else if (!POSTHOG_KEY) {
    console.info('ℹ️ [Tracker] PostHog key not set. Operating in Supabase audit ledger mode.');
  }
}

// 2. Browser & Device detection helpers
function getDeviceType(): string {
  const ua = navigator.userAgent;
  if (/mobile/i.test(ua)) return 'Mobile Phone';
  if (/ipad|tablet/i.test(ua)) return 'Tablet';
  if (/macintosh|mac os x/i.test(ua)) return 'Mac Desktop';
  if (/windows/i.test(ua)) return 'Windows PC';
  if (/linux/i.test(ua)) return 'Linux PC';
  return 'Desktop';
}

function getBrowserName(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Edg/')) return 'Microsoft Edge';
  if (ua.includes('Chrome/')) return 'Google Chrome';
  if (ua.includes('Firefox/')) return 'Mozilla Firefox';
  if (ua.includes('Safari/') && !ua.includes('Chrome')) return 'Apple Safari';
  return 'Browser';
}

// 3. Fetch public IP & Geo location
async function fetchGeoInfo(): Promise<GeoInfo> {
  // Primary service: ipwho.is (fast, no rate-limiting, returns city/state/isp)
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3500);
    const res = await fetch('https://ipwho.is/', { signal: controller.signal });
    clearTimeout(timeoutId);

    if (res.ok) {
      const data = await res.json();
      if (data.success !== false && data.ip) {
        return {
          ip: data.ip,
          city: data.city || '',
          region: data.region || '',
          country: data.country || '',
          isp: data.connection?.isp || data.connection?.org || '',
        };
      }
    }
  } catch (_err) {
    // Fallback if ipwho.is is blocked or fails
  }

  // Fallback service: api.ipify.org
  try {
    const res = await fetch('https://api.ipify.org?format=json');
    if (res.ok) {
      const data = await res.json();
      return { ip: data.ip || 'Unknown' };
    }
  } catch (_err) {
    // Fallback failed
  }

  return { ip: 'Unknown' };
}

// 4. Record user login into Supabase login_audit_logs and PostHog
export async function recordLoginAudit(user: {
  id?: string;
  email: string;
  name?: string;
  role?: string;
}): Promise<void> {
  try {
    const sessionLogged = sessionStorage.getItem('ski_audit_logged_' + user.email);
    if (sessionLogged) {
      return; // Already logged this session
    }

    const geo = await fetchGeoInfo();
    const deviceType = getDeviceType();
    const browser = getBrowserName();
    const timestamp = new Date().toISOString();

    // A. PostHog Track & Identify (External Cloud Dashboard)
    if (isPostHogInitialized) {
      try {
        posthog.identify(user.email, {
          email: user.email,
          name: user.name || user.email.split('@')[0],
          role: user.role || 'user',
          ip: geo.ip,
          city: geo.city,
          country: geo.country,
        });
        posthog.capture('user_login', {
          email: user.email,
          name: user.name,
          role: user.role,
          ip: geo.ip,
          city: geo.city,
          country: geo.country,
          device: deviceType,
          browser: browser,
        });
      } catch (phErr) {
        console.warn('PostHog capture warning:', phErr);
      }
    }

    // B. Supabase Persistent Audit Table
    const record: LoginAuditRecord = {
      user_id: user.id,
      email: user.email,
      user_name: user.name || user.email.split('@')[0],
      role: user.role || 'user',
      ip_address: geo.ip,
      city: geo.city || 'Unknown',
      region: geo.region || '',
      country: geo.country || 'Unknown',
      isp: geo.isp || '',
      user_agent: navigator.userAgent,
      device_type: deviceType,
      browser: browser,
      login_at: timestamp,
    };

    const { error } = await supabase.from('login_audit_logs').insert([record]);
    if (error) {
      console.warn('⚠️ [Tracker] Supabase audit log insert notice (table may need creation):', error.message);
    } else {
      console.log('✅ [Tracker] Login successfully audited in Supabase for:', user.email);
    }

    sessionStorage.setItem('ski_audit_logged_' + user.email, timestamp);
  } catch (err) {
    console.warn('⚠️ [Tracker] Login audit logging encountered an error (non-blocking):', err);
  }
}

// 5. Fetch recent audit logs from Supabase
export async function getLoginAuditLogs(limit = 50): Promise<LoginAuditRecord[]> {
  try {
    const { data, error } = await supabase
      .from('login_audit_logs')
      .select('*')
      .order('login_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.warn('Could not fetch audit logs:', error.message);
      return [];
    }
    return (data as LoginAuditRecord[]) || [];
  } catch (err) {
    console.error('Error fetching audit logs:', err);
    return [];
  }
}

// 6. Operational Activity Logging (Quotations, Discount Overrides, File Exports)
export interface OperationalEvent {
  id: string;
  user_email: string;
  user_name?: string;
  role?: string;
  action: string;
  category: 'quotation' | 'discount' | 'export' | 'auth' | 'system';
  details: string;
  timestamp: string;
}

const LOCAL_ACTIVITY_KEY = 'ski_admin_activity_events';

export async function recordOperationalEvent(event: Omit<OperationalEvent, 'id' | 'timestamp'>): Promise<void> {
  try {
    const newRecord: OperationalEvent = {
      ...event,
      id: 'act_' + Date.now() + '_' + Math.random().toString(36).substring(2, 7),
      timestamp: new Date().toISOString(),
    };

    // Store in localStorage for instant admin stream
    try {
      const existing = JSON.parse(localStorage.getItem(LOCAL_ACTIVITY_KEY) || '[]');
      const updated = [newRecord, ...existing].slice(0, 200); // keep last 200 events
      localStorage.setItem(LOCAL_ACTIVITY_KEY, JSON.stringify(updated));
    } catch (_lsErr) {
      // Local storage full or private mode
    }

    // PostHog track
    if (isPostHogInitialized) {
      try {
        posthog.capture(event.action, {
          category: event.category,
          details: event.details,
          user_email: event.user_email,
        });
      } catch (_phErr) {
        // Non-blocking
      }
    }
  } catch (err) {
    console.warn('Error recording operational event:', err);
  }
}

export function getOperationalEvents(): OperationalEvent[] {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_ACTIVITY_KEY) || '[]');
  } catch (_err) {
    return [];
  }
}

